// Everyday Services (commerce) platform-operator API — kill switches and the
// reconciliation queue, via the gateway at /api/v1/commerce-ops/* (platform
// scope; commerce:ops, and commerce:ops:resolve to resolve a case).
// Per-tenant enablement is NOT here: that is the tenant's Features & Products
// toggle (product `commerce`).
import axios from 'axios';
import type {
  CommerceSwitch, ProviderInfo, ProviderProduct, ProviderProductInput, ResolveOutcome,
} from '../pages/commerce/everydayServicesLogic';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);
const BASE = `${API_V1}/commerce-ops`;

export interface ReconCase {
  id: string;
  order_id: string;
  tenant_id: string;
  reason: string;
  status: 'open' | 'pending_provider_data' | 'resolved' | string;
  provider_code: string;
  product_type: string;
  recipient: string; // masked by the service
  amount_minor: number;
  currency: string;
  opened_at: string;
}

export interface ResolveBody {
  outcome: ResolveOutcome;
  evidence_ref: string;
  note?: string;
  /** Electricity token — only for ELECTRICITY + delivered. Never log it. */
  token?: string;
  units?: string;
}

export const listSwitches = () =>
  axios.get<{ items: CommerceSwitch[] }>(`${BASE}/switches`).then((r) => r.data.items ?? []);

export const setSwitch = (scope: string, enabled: boolean, reason: string) =>
  axios.put<{ scope: string; enabled: boolean }>(`${BASE}/switches`, { scope, enabled, reason }).then((r) => r.data);

export const listReconCases = () =>
  axios.get<{ items: ReconCase[] }>(`${BASE}/recon-cases`).then((r) => r.data.items ?? []);

export const resolveReconCase = (id: string, body: ResolveBody) =>
  axios
    .post<ReconCase>(`${BASE}/recon-cases/${encodeURIComponent(id)}/resolve`, body)
    .then((r) => r.data);

export const listProviderProducts = () =>
  axios
    .get<{ items: ProviderProduct[]; providers: ProviderInfo[] }>(`${BASE}/provider-products`)
    .then((r) => ({ items: r.data.items ?? [], providers: r.data.providers ?? [] }));

export const upsertProviderProduct = (body: ProviderProductInput) =>
  axios.put<ProviderProduct>(`${BASE}/provider-products`, body).then((r) => r.data);
